import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'
import type { User } from '@/types'

/**
 * Auto "At The Lake": when a member has opted in (User.autoInResidence),
 * every portal visit quietly asks the browser where they are. If they're
 * within the Shorewood radius, the backend refreshes their presence window
 * (48h rolling, server-gated — see backend utils/autoResidence). Presence
 * then decays on its own after they leave; there is no auto-"off".
 *
 * IP geolocation was considered and rejected: cellular IPs routinely
 * resolve hundreds of miles from the phone. navigator.geolocation is
 * GPS-accurate on the devices members actually bring to the lake.
 */

// Shorewood Drive, Douglas MI — same default coords as useLakeConditions.
const LAKE_LAT = Number((import.meta as any).env?.VITE_LAKE_LAT ?? 42.6428)
const LAKE_LNG = Number((import.meta as any).env?.VITE_LAKE_LNG ?? -86.205)

// Covers the whole association, the beach, and a stroll into Douglas
// without reaching across the lake or down the highway.
const AT_LAKE_RADIUS_MILES = 1.5

// Re-check at most every 30 minutes per device; presence windows are 48h,
// so anything more eager is wasted battery and API chatter.
const CHECK_INTERVAL_MS = 30 * 60 * 1000
const CHECKED_AT_KEY = 'shorewood:atLakeAutoCheckedAt'

/** Great-circle distance in miles (haversine). */
export function distanceMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const R = 3958.8 // Earth radius, miles
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

/** Is this position within the Shorewood radius? */
export function isAtTheLake(latitude: number, longitude: number): boolean {
  return (
    distanceMiles(latitude, longitude, LAKE_LAT, LAKE_LNG) <=
    AT_LAKE_RADIUS_MILES
  )
}

/**
 * One-shot browser geolocation as a promise. Coarse accuracy is plenty for
 * a 1.5-mile radius; a 10-minute-old cached fix is fine too.
 */
export function getPositionOnce(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation unavailable'))
      return
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10_000,
      maximumAge: 10 * 60 * 1000,
    })
  })
}

export type AtLakeCheckResult =
  | { status: 'refreshed'; user: User }
  | { status: 'away' }
  | { status: 'denied' }
  | { status: 'unavailable' }

/**
 * Locate the member and, if they're at Shorewood, ask the backend to refresh
 * their presence. Used by both the background hook and the opt-in prompt.
 */
export async function checkAndRefreshPresence(): Promise<AtLakeCheckResult> {
  let position: GeolocationPosition
  try {
    position = await getPositionOnce()
  } catch (err: any) {
    // GeolocationPositionError.PERMISSION_DENIED === 1
    return err?.code === 1 ? { status: 'denied' } : { status: 'unavailable' }
  }
  if (!isAtTheLake(position.coords.latitude, position.coords.longitude)) {
    return { status: 'away' }
  }
  const user = await apiService.refreshAtLakePresence()
  return { status: 'refreshed', user }
}

/**
 * Background auto-detect. Mount once inside the authenticated layout (via
 * <AtLakeAutoPrompt />). Runs only for opted-in members, throttled per
 * device via localStorage. Failures are silent by design — this is a
 * convenience, not a workflow.
 */
export function useAutoAtLake() {
  const optedIn = useAuthStore((s) => s.user?.autoInResidence === true)
  const updateUser = useAuthStore((s) => s.updateUser)
  const qc = useQueryClient()

  useEffect(() => {
    if (!optedIn) return

    // Per-device throttle. localStorage can throw (private mode); treat
    // that as "not throttled".
    try {
      const last = Number(localStorage.getItem(CHECKED_AT_KEY) ?? 0)
      if (Date.now() - last < CHECK_INTERVAL_MS) return
      localStorage.setItem(CHECKED_AT_KEY, String(Date.now()))
    } catch {
      /* ignore */
    }

    let cancelled = false
    checkAndRefreshPresence()
      .then((result) => {
        if (cancelled || result.status !== 'refreshed') return
        updateUser({
          inResidenceUntil: result.user.inResidenceUntil ?? null,
        })
        qc.invalidateQueries({ queryKey: ['directory'] })
      })
      .catch(() => {
        /* silent: never surface background presence errors */
      })
    return () => {
      cancelled = true
    }
  }, [optedIn, updateUser, qc])
}
